/* ============================================================================
   Lake Effect Lures — general theme behaviour.

   Page chrome (announcement rotator, header, hero) lives in le-layout.js.
   Cart and product behaviour lives in le-commerce.js. This file holds the
   cross-cutting bits: scroll reveal, external-link affordances, and the shared
   `le:cart:updated` event contract.

   Motion rule from the design system: short and mechanical, never bouncy, and
   nothing moves at all for visitors who ask for reduced motion.
   ========================================================================== */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------ scroll reveal --- */

  function initReveal() {
    var targets = document.querySelectorAll('[data-le-reveal]');
    if (!targets.length) return;

    // No IntersectionObserver, or the visitor asked for reduced motion: show
    // everything immediately. The reveal is decoration, never a gate on content.
    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, function (el) {
        el.setAttribute('data-le-revealed', 'true');
      });
      return;
    }

    // Arm the CSS only now that we know we can actually un-hide things again.
    document.documentElement.setAttribute('data-le-reveal-armed', '');

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute('data-le-revealed', 'true');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    Array.prototype.forEach.call(targets, function (el) {
      observer.observe(el);
    });
  }

  /* -------------------------------------------------- quantity steppers --- */

  /* The stepper's `<input>` works on its own with JS off — this only wires the
     +/- buttons. It deliberately stops at changing the value and firing `change`:
     whoever owns the surrounding form (the cart, the buy box) listens for that
     and decides what a quantity change means there. */
  function stepQuantity(stepper, delta) {
    var input = stepper.querySelector('[data-le-qty-input]');
    if (!input) return;

    var min = parseInt(stepper.getAttribute('data-min'), 10);
    var max = parseInt(stepper.getAttribute('data-max'), 10);
    if (isNaN(min)) min = 0;
    if (isNaN(max)) max = Infinity;

    var current = parseInt(input.value, 10);
    if (isNaN(current)) current = min;

    var next = Math.min(max, Math.max(min, current + delta));
    if (next === current) return;

    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function syncStepperButtons(stepper) {
    var input = stepper.querySelector('[data-le-qty-input]');
    if (!input) return;

    var min = parseInt(stepper.getAttribute('data-min'), 10);
    var max = parseInt(stepper.getAttribute('data-max'), 10);
    var current = parseInt(input.value, 10);
    if (isNaN(current)) return;

    var dec = stepper.querySelector('[data-le-qty-decrease]');
    var inc = stepper.querySelector('[data-le-qty-increase]');
    if (dec && !isNaN(min)) dec.disabled = current <= min;
    if (inc && !isNaN(max)) inc.disabled = current >= max;
  }

  function initQuantitySteppers() {
    // Delegated, so steppers injected later (cart re-render, quick view, theme
    // editor) work without re-binding.
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-le-qty-decrease],[data-le-qty-increase]');
      if (!button) return;

      var stepper = button.closest('[data-le-qty-stepper]');
      if (!stepper) return;

      event.preventDefault();
      stepQuantity(stepper, button.hasAttribute('data-le-qty-increase') ? 1 : -1);
      syncStepperButtons(stepper);
    });

    document.addEventListener('change', function (event) {
      var input = event.target.closest('[data-le-qty-input]');
      if (!input) return;

      var stepper = input.closest('[data-le-qty-stepper]');
      if (!stepper) return;

      // Typed input can be junk or out of range; clamp it before anyone reads it.
      var min = parseInt(stepper.getAttribute('data-min'), 10);
      var max = parseInt(stepper.getAttribute('data-max'), 10);
      var value = parseInt(input.value, 10);
      if (isNaN(value)) value = isNaN(min) ? 0 : min;
      if (!isNaN(min)) value = Math.max(min, value);
      if (!isNaN(max)) value = Math.min(max, value);
      input.value = value;

      syncStepperButtons(stepper);
    });

    Array.prototype.forEach.call(document.querySelectorAll('[data-le-qty-stepper]'), syncStepperButtons);
  }

  /* ------------------------------------------- links that open elsewhere --- */

  function initExternalLinks() {
    var message = document.getElementById('a11y-new-window-message');
    if (!message) return;

    Array.prototype.forEach.call(document.querySelectorAll('a[target="_blank"]'), function (link) {
      if (link.getAttribute('rel') === null) {
        link.setAttribute('rel', 'noopener noreferrer');
      }
      if (!link.hasAttribute('aria-describedby')) {
        link.setAttribute('aria-describedby', 'a11y-new-window-message');
      }
    });
  }

  /* ---------------------------------------------------- product marquee --- */

  /* The strip's motion is pure CSS, so everything here is enhancement:
     - the visible pause/play control, because WCAG 2.2.2 wants an explicit one
       and the CSS hover/focus-within pause is not reachable from every input;
     - a static, natively scrollable mode while focus is inside the strip, so the
       browser can bring a focused card into view instead of fighting a moving
       ancestor;
     - top-ups for short product lists, which would otherwise leave dead space
       where the two tracks meet;
     - an IntersectionObserver that parks the animation while the section is off
       screen, the same way le-layout.js `createRotator` does.

     Visitors who asked for reduced motion never reach any of it: the CSS has
     already turned the strip into a plain scroller with the toggle hidden. */

  function deactivateClone(item) {
    item.setAttribute('aria-hidden', 'true');
    Array.prototype.forEach.call(item.querySelectorAll('a, button, input, select, textarea'), function (el) {
      el.setAttribute('tabindex', '-1');
    });
  }

  // A track narrower than the marquee leaves a gap at the seam every loop.
  // Repeat its items until it fills — in both tracks, so the two stay exactly
  // the same width and `translateX(-50%)` still lands on the seam.
  function fillTracks(tracks, minWidth) {
    var originals = tracks.map(function (track) {
      return Array.prototype.slice.call(track.children);
    });
    if (!originals[0].length) return;

    var passes = 0;
    while (tracks[0].offsetWidth < minWidth && passes < 20) {
      tracks.forEach(function (track, i) {
        originals[i].forEach(function (item) {
          var copy = item.cloneNode(true);
          deactivateClone(copy);
          track.appendChild(copy);
        });
      });
      passes += 1;
    }
  }

  function setupMarquee(marquee) {
    // The theme editor re-runs this on every section load; only wire each
    // marquee once, or the toggle would flip twice per click.
    if (marquee.hasAttribute('data-le-marquee-ready')) return;

    var rail = marquee.querySelector('[data-le-marquee-rail]');
    var toggle = marquee.querySelector('[data-le-marquee-toggle]');
    var tracks = Array.prototype.slice.call(
      marquee.querySelectorAll('[data-le-marquee-track],[data-le-marquee-clone]')
    );
    if (!rail || tracks.length < 2) return;

    marquee.setAttribute('data-le-marquee-ready', 'true');
    fillTracks(tracks, marquee.offsetWidth);

    if (toggle) {
      toggle.addEventListener('click', function () {
        var paused = marquee.hasAttribute('data-le-marquee-paused');
        if (paused) {
          marquee.removeAttribute('data-le-marquee-paused');
        } else {
          marquee.setAttribute('data-le-marquee-paused', 'true');
        }
        // Pressed means "pause is engaged"; the CSS swaps the label to Play.
        toggle.setAttribute('aria-pressed', paused ? 'false' : 'true');
      });
    }

    rail.addEventListener('focusin', function () {
      marquee.setAttribute('data-le-marquee-static', 'true');
    });

    rail.addEventListener('focusout', function (event) {
      // `relatedTarget` is the element gaining focus, and is null when focus
      // leaves the document entirely — both mean focus has left the strip.
      if (rail.contains(event.relatedTarget)) return;
      marquee.removeAttribute('data-le-marquee-static');
    });

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              marquee.removeAttribute('data-le-marquee-offscreen');
            } else {
              marquee.setAttribute('data-le-marquee-offscreen', 'true');
            }
          });
        },
        { threshold: 0.1 }
      );
      observer.observe(marquee);
    }
  }

  function initMarquee() {
    var marquees = document.querySelectorAll('[data-le-marquee]');
    if (!marquees.length) return;
    if (prefersReducedMotion.matches) return;

    Array.prototype.forEach.call(marquees, setupMarquee);
  }

  /* ---------------------------------------------------- video lightbox --- */

  /* Native <dialog> already traps focus and closes on Escape. This wires the
     open triggers, moves the clip into the player with sound, and pauses any
     muted strip loops while the overlay is up. */
  function setupLightbox(root) {
    if (root.hasAttribute('data-le-lightbox-ready')) return;

    var dialog = root.querySelector('[data-le-lightbox-dialog]');
    var player = root.querySelector('[data-le-lightbox-player]');
    var closeBtn = root.querySelector('[data-le-lightbox-close]');
    var triggers = root.querySelectorAll('[data-le-lightbox-open]');
    if (!dialog || !player || !triggers.length) return;

    root.setAttribute('data-le-lightbox-ready', 'true');
    var lastTrigger = null;

    function pauseLoops() {
      Array.prototype.forEach.call(root.querySelectorAll('[data-le-whatnot-loop]'), function (video) {
        video.pause();
      });
    }

    function resumeLoops() {
      if (prefersReducedMotion.matches) return;
      Array.prototype.forEach.call(root.querySelectorAll('[data-le-whatnot-loop]'), function (video) {
        if (video.hasAttribute('data-le-loop-visible')) {
          var playPromise = video.play();
          if (playPromise && playPromise.catch) playPromise.catch(function () {});
        }
      });
    }

    function openFrom(trigger) {
      lastTrigger = trigger;
      var src = trigger.getAttribute('data-le-lightbox-src');
      var poster = trigger.getAttribute('data-le-lightbox-poster') || '';
      var title = trigger.getAttribute('data-le-lightbox-title') || '';
      if (!src) return;

      pauseLoops();
      player.src = src;
      if (poster) {
        player.setAttribute('poster', poster);
      } else {
        player.removeAttribute('poster');
      }
      if (title) player.setAttribute('aria-label', title);
      dialog.showModal();
      player.currentTime = 0;
      var playPromise = player.play();
      if (playPromise && playPromise.catch) playPromise.catch(function () {});
    }

    function closeLightbox() {
      player.pause();
      player.removeAttribute('src');
      player.load();
      if (dialog.open) dialog.close();
    }

    Array.prototype.forEach.call(triggers, function (trigger) {
      trigger.addEventListener('click', function () {
        openFrom(trigger);
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', closeLightbox);

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeLightbox();
    });

    dialog.addEventListener('close', function () {
      player.pause();
      player.removeAttribute('src');
      player.load();
      resumeLoops();
      if (lastTrigger) lastTrigger.focus();
    });
  }

  function setupWhatnotLoop(video) {
    if (video.hasAttribute('data-le-loop-ready')) return;
    video.setAttribute('data-le-loop-ready', 'true');

    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var dialogOpen = document.querySelector('[data-le-lightbox-dialog][open]');
          if (entry.isIntersecting && !dialogOpen) {
            video.setAttribute('data-le-loop-visible', 'true');
            var playPromise = video.play();
            if (playPromise && playPromise.then) {
              playPromise
                .then(function () {
                  video.classList.add('is-playing');
                })
                .catch(function () {});
            }
          } else {
            video.removeAttribute('data-le-loop-visible');
            video.pause();
          }
        });
      },
      { threshold: 0.45 }
    );
    observer.observe(video);
  }

  function initLightbox() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-le-lightbox]'), setupLightbox);
    Array.prototype.forEach.call(document.querySelectorAll('[data-le-whatnot-loop]'), setupWhatnotLoop);
  }

  /* ---------------------------------------------------- theme editor ------ */

  function initThemeEditorHooks() {
    if (!window.Shopify || !window.Shopify.designMode) return;

    // Re-run the enhancements when a merchant adds or edits a section, otherwise
    // freshly-injected markup keeps its pre-reveal state and looks broken.
    document.addEventListener('shopify:section:load', function () {
      initReveal();
      initExternalLinks();
      initMarquee();
      initLightbox();
    });
  }

  function init() {
    initReveal();
    initQuantitySteppers();
    initExternalLinks();
    initMarquee();
    initLightbox();
    initThemeEditorHooks();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
